import { useEffect, useMemo, useState } from "react";
import FormInput from "../../../components/controls/FormInput";
import ImageUploader from "../../../components/controls/ImageUploader";
import DateTimePicker from "../../../components/controls/DateTimePicker";
import StylizedEditor from "../../../components/controls/StylizedEditor";
import DropdownMenu from "../../../components/controls/DropdownMenu";
import Modal from "../../../components/controls/Modal";
import Button from "../../../components/controls/Button";

const getTagTitle = (tagId, tags) => {
  const found = tags.find((t) => String(t.id) === String(tagId));
  return found?.name || "Sélectionner un tag";
};

const getCompanyTitle = (companyId, companies) => {
  const found = companies.find((c) => String(c.id) === String(companyId));
  return found?.name || "Sélectionner une compagnie";
};

const safeParseTiptapJsonString = (value) => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (parsed && parsed.type === "doc") return parsed;
    return null;
  } catch {
    return null;
  }
};

const plainTextToTiptapJsonString = (plainText) => {
  const text = (plainText || "").trim();
  const doc = {
    type: "doc",
    content: text
      ? [
          {
            type: "paragraph",
            content: [{ type: "text", text }],
          },
        ]
      : [],
  };
  return JSON.stringify(doc);
};

const normalizeIncomingContentToTiptapJsonString = (raw) => {
  const parsed = safeParseTiptapJsonString(raw);
  if (parsed) return JSON.stringify(parsed);
  return plainTextToTiptapJsonString(raw || "");
};

export default function ProviderNewsModal({
  isOpen,
  onClose,
  onSave,
  newsItem,
  userId,
}) {
  const isEditing = !!newsItem;

  const [title, setTitle] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [text, setText] = useState("");
  const [tag, setTag] = useState("");
  const [companyId, setCompanyId] = useState(0);
  const [publishDate, setPublishDate] = useState(null);

  const [companies, setCompanies] = useState([]);
  const [tags, setTags] = useState([]);

  const safeFetch = async (url) => {
    try {
      const res = await fetch(url, {
        headers: { "Content-Type": "application/json", "X-User-Id": userId },
      });
      if (!res.ok) return null;
      const txt = await res.text();
      if (!txt) return null;
      return JSON.parse(txt);
    } catch {
      return null;
    }
  };

  const loadCompanies = async () => {
    const data = await safeFetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/Company?userId=${userId}`
    );
    const mapped = Array.isArray(data)
      ? data.map((c) => ({ id: c.id, name: c.name }))
      : [];
    setCompanies(mapped);
  };

  const loadTags = async () => {
    const data = await safeFetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/News/Tags`
    );
    if (!Array.isArray(data)) return setTags([]);
    setTags(data.map((t) => ({ id: t.id, name: t.nameFr })));
  };

  useEffect(() => {
    if (!userId) return;
    loadCompanies();
    loadTags();
  }, [userId]);

  useEffect(() => {
    if (newsItem) {
      setTitle(newsItem.title || "");
      setImageUrl(newsItem.imageUrl || "");
      setText(
        normalizeIncomingContentToTiptapJsonString(
          newsItem.text || newsItem.content || ""
        )
      );
      setTag(newsItem.tagId || "");
      setCompanyId(newsItem.companyId || 0);
      setPublishDate(
        newsItem.publishDate ? new Date(newsItem.publishDate) : null
      );
      return;
    }

    setTitle("");
    setImageUrl("");
    setText(plainTextToTiptapJsonString(""));
    setTag("");
    setCompanyId(0);
    setPublishDate(null);
  }, [newsItem]);

  const isEmptyEditorJsonString = (jsonString) => {
    const parsed = safeParseTiptapJsonString(jsonString);
    if (!parsed) return !(jsonString || "").trim();
    const content = parsed.content || [];
    if (!content.length) return true;
    if (content.length !== 1 || content[0]?.type !== "paragraph") return false;
    const inner = content[0]?.content || [];
    if (!inner.length) return true;
    const hasText = inner.some(
      (n) => n?.type === "text" && (n.text || "").trim()
    );
    return !hasText;
  };

  const handleSave = async () => {
    if (!title.trim()) return alert("Veuillez entrer un titre.");
    if (!imageUrl.trim()) return alert("Veuillez ajouter une image.");
    if (isEmptyEditorJsonString(text))
      return alert("Veuillez entrer un texte.");
    if (!tag) return alert("Veuillez sélectionner un tag.");
    if (!companyId) return alert("Veuillez sélectionner une compagnie.");

    const payload = {
      id: newsItem?.id || null,
      title,
      imageUrl,
      text,
      tagId: tag,
      companyId: Number(companyId),
      publishDate: publishDate ? new Date(publishDate) : null,
      userId,
      status: "draft",
    };

    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/News`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-User-Id": userId },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const message = await res.text();
      alert("Erreur : " + message);
      return;
    }

    onSave();
  };

  const plainCharCount = useMemo(() => {
    const parsed = safeParseTiptapJsonString(text);
    if (!parsed) return 0;
    const walk = (node) => {
      if (!node) return "";
      if (Array.isArray(node)) return node.map(walk).join("");
      if (node.type === "text") return node.text || "";
      if (node.content) return walk(node.content);
      return "";
    };
    return walk(parsed).replace(/\s+/g, " ").trim().length;
  }, [text]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <Button
        as="button"
        type="button"
        color="secondary"
        size="md"
        onClick={onClose}
      >
        Annuler
      </Button>
      <Button
        as="button"
        type="button"
        color="primary"
        size="md"
        onClick={handleSave}
      >
        Enregistrer
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? "Modifier l'actualité" : "Ajouter une actualité"}
      size="xl"
      footer={footer}
      showCloseButton
    >
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="w-full lg:w-[360px] flex-shrink-0 flex flex-col gap-4">
          <FormInput
            label="Titre de l'article"
            name="title"
            value={title}
            onChange={(n, v) => setTitle(v)}
            required
            maxLength={100}
          />

          <DateTimePicker
            label="Date de publication"
            value={publishDate}
            onChange={setPublishDate}
          />

          <div>
            <label className="block text-sm font-medium text-primary mb-1">
              Tag <span className="text-muted">*</span>
            </label>
            <DropdownMenu
              title={getTagTitle(tag, tags)}
              isDropdown
              withSearch
              items={tags.map((t) => ({
                key: t.id,
                label: t.name,
                onClick: () => setTag(t.id),
              }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">
              Compagnie <span className="text-muted">*</span>
            </label>
            <DropdownMenu
              title={getCompanyTitle(companyId, companies)}
              isDropdown
              withSearch
              searchPlaceholder="Rechercher une compagnie..."
              items={companies.map((c) => ({
                key: c.id,
                label: c.name,
                onClick: () => setCompanyId(Number(c.id)),
              }))}
            />
          </div>

          <ImageUploader
            initialImageUrl={imageUrl}
            onTempImageChange={(url) => setImageUrl(url || "")}
            bucket="actualites"
          />

          <p className="text-xs text-gray-500">
            Toutes les actualités seront validées par notre équipe avant
            publication.
          </p>
        </div>

        <div className="flex-1 min-h-[520px] flex flex-col">
          <div className="flex-1 min-h-0">
            <StylizedEditor
              label="Texte"
              valueJson={text}
              onChangeJson={setText}
              fullHeight
            />
          </div>

          <div className="text-right text-xs text-gray-500 mt-2">
            {Math.min(plainCharCount, 10000)} / 10000
          </div>
        </div>
      </div>
    </Modal>
  );
}
